// A bed is one cue's file playing as a loop, behind a single fader the engine uses to crossfade
// between cues.
//
//   streamed (crossfade files)               seamless (CC0 files)
//   voice 1 ─┐                                buffer source (loop = true)
//   voice 2 ─┴─ bed fader ─▶ music bus        └─ bed fader ─▶ music bus
//
// A streamed bed starts a new <audio> voice shortly before the old one ends and blends them; a
// seamless bed is decoded once and the audio clock loops it sample-exact.

import { still, fadeTo } from './fader.ts';
import type { Fade } from './fade.ts';
import { loopKind, untilNextVoice } from './loop.ts';
import type { BufferLike, BufferSourceLike, ContextLike, Deps, ElementLike, GainLike, NodeLike } from './webaudio.ts';

/** Buffer the next voice this long before it is needed, so it starts the moment it is asked to. */
export const PRELOAD_S = 8;

const TICK_MAX_S = 1;
const TICK_MIN_S = 0.05;
const MS_PER_S = 1000;

// A loop blend never gets shorter than this, even when the timer ran late.
const MIN_FADE_S = 0.1;

// A faded-out voice is closed this long after its fade, so the last samples are not cut.
const RETIRE_MARGIN_S = 0.2;

const BLOCKED_ERROR = 'NotAllowedError';

export interface Bed {
  /** Make the bed audible, fading in over `fadeSeconds`. */
  start(fadeSeconds: number): void;
  /** Fade out over `fadeSeconds`, then stop and free everything; `done` runs once it has stopped. */
  release(fadeSeconds: number, done?: () => void): void;
  /** Stop and free everything now. Safe to call more than once, or before `start`. */
  stop(): void;
}

export interface BedEnv {
  ctx: ContextLike;
  deps: Deps;
  /** Where beds connect: the music bus. */
  out: NodeLike;
}

export interface BedSpec {
  url: string;
  /** Length of the file, from the manifest; used until the element knows its own duration. */
  seconds: number;
  /** Manifest crossfade: how the file loops (see loop.ts). */
  crossfade: number;
  /** A streamed element reported an error after it had started playing. */
  onFail(): void;
}

/** True when `error` is the browser refusing to play because the page has had no user gesture. */
export function isBlocked(error: unknown): boolean {
  return error instanceof Error && error.name === BLOCKED_ERROR;
}

/** The bed's own gain node, wired to the bus and silent until the first fade. */
function makeFader(env: BedEnv) {
  const node = env.ctx.createGain();
  const born = env.ctx.currentTime;
  node.gain.setValueAtTime(0, born);
  node.connect(env.out);

  let fade: Fade = still(0, born);
  return {
    node,
    to(level: number, seconds: number): void {
      fade = fadeTo(node.gain, fade, level, env.ctx.currentTime, seconds);
    },
  };
}

/** Prepare a bed. Resolves when it can start at once; rejects if the file cannot be played. */
export async function loadBed(env: BedEnv, spec: BedSpec): Promise<Bed> {
  if (loopKind(spec.crossfade) === 'seamless') {
    return seamlessBed(env, await decode(env, spec.url));
  }
  return streamedBed(env, spec);
}

async function decode(env: BedEnv, url: string): Promise<BufferLike> {
  return env.ctx.decodeAudioData(await env.deps.fetchBytes(url));
}

function seamlessBed(env: BedEnv, buffer: BufferLike): Bed {
  const fader = makeFader(env);
  const source: BufferSourceLike = env.ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.connect(fader.node);

  let started = false;
  let stopped = false;
  let retire: number | null = null;
  let onStopped: (() => void) | undefined;

  function stop(): void {
    if (stopped) {
      return;
    }
    stopped = true;
    if (retire !== null) {
      env.deps.clearTimer(retire);
    }

    // Stopping a source that never started throws.
    if (started) {
      source.stop();
    }
    source.disconnect();
    fader.node.disconnect();
    onStopped?.();
  }

  return {
    start(fadeSeconds) {
      if (started || stopped) {
        return;
      }
      started = true;
      source.start();
      fader.to(1, fadeSeconds);
    },
    release(fadeSeconds, done) {
      if (stopped) {
        done?.();
        return;
      }
      onStopped = done;
      fader.to(0, fadeSeconds);
      retire = env.deps.setTimer(stop, (fadeSeconds + RETIRE_MARGIN_S) * MS_PER_S);
    },
    stop,
  };
}

interface Voice {
  element: ElementLike;
  node: NodeLike;
  gain: GainLike;
  fade: Fade;
}

async function streamedBed(env: BedEnv, spec: BedSpec): Promise<Bed> {
  const fader = makeFader(env);
  let voices: Voice[] = [];
  let preloaded: ElementLike | null = null;
  let tick: number | null = null;
  const retiring = new Set<number>();
  let started = false;
  let stopped = false;
  let onStopped: (() => void) | undefined;

  function open(element: ElementLike, level: number): Voice {
    const born = env.ctx.currentTime;
    const node = env.ctx.createMediaElementSource(element);
    const gain = env.ctx.createGain();
    gain.gain.setValueAtTime(level, born);
    node.connect(gain);
    gain.connect(fader.node);
    element.onerror = spec.onFail;

    const voice = { element, node, gain, fade: still(level, born) };
    voices.push(voice);
    return voice;
  }

  function close(voice: Voice): void {
    voice.element.onerror = null;
    voice.element.dispose();
    voice.node.disconnect();
    voice.gain.disconnect();
    voices = voices.filter((v) => v !== voice);
  }

  function blend(voice: Voice, level: number, seconds: number): void {
    voice.fade = fadeTo(voice.gain.gain, voice.fade, level, env.ctx.currentTime, seconds);
  }

  // The old voice fades out as the new one fades in; both stop being heard at the same moment.
  function handOver(lead: Voice, duration: number): void {
    const room = duration - lead.element.currentTime;
    const seconds = Math.max(MIN_FADE_S, Math.min(spec.crossfade, room));

    const element = preloaded ?? env.deps.createElement(spec.url);
    preloaded = null;
    const next = open(element, 0);
    element.play().catch(failed);
    blend(next, 1, seconds);

    blend(lead, 0, seconds);
    const id = env.deps.setTimer(() => {
      retiring.delete(id);
      close(lead);
    }, (seconds + RETIRE_MARGIN_S) * MS_PER_S);
    retiring.add(id);
  }

  // A play() cut short by our own stop() rejects too; that is not a failure.
  function failed(): void {
    if (!stopped) {
      spec.onFail();
    }
  }

  function lengthOf(element: ElementLike): number {
    return Number.isFinite(element.duration) && element.duration > 0 ? element.duration : spec.seconds;
  }

  function watch(): void {
    tick = null;
    const lead = voices[voices.length - 1];
    if (stopped || lead === undefined) {
      return;
    }

    const duration = lengthOf(lead.element);
    const wait = untilNextVoice(lead.element.currentTime, duration, spec.crossfade);
    if (wait <= 0) {
      handOver(lead, duration);
    } else if (wait <= PRELOAD_S && preloaded === null) {
      preloaded = env.deps.createElement(spec.url);
    }

    const next = Math.min(TICK_MAX_S, Math.max(TICK_MIN_S, wait));
    tick = env.deps.setTimer(watch, next * MS_PER_S);
  }

  function stop(): void {
    if (stopped) {
      return;
    }
    stopped = true;
    if (tick !== null) {
      env.deps.clearTimer(tick);
    }
    retiring.forEach((id) => env.deps.clearTimer(id));
    retiring.clear();

    preloaded?.dispose();
    preloaded = null;
    [...voices].forEach(close);
    fader.node.disconnect();
    onStopped?.();
  }

  // Play behind a silent fader first: `play()` resolves once data flows, so the fade-in that
  // follows starts on real sound instead of on a download.
  const first = open(env.deps.createElement(spec.url), 1);
  try {
    await first.element.play();
  } catch (error) {
    stop();
    throw error;
  }

  return {
    start(fadeSeconds) {
      if (stopped || started) {
        return;
      }
      started = true;
      fader.to(1, fadeSeconds);
      tick = env.deps.setTimer(watch, TICK_MAX_S * MS_PER_S);
    },
    release(fadeSeconds, done) {
      if (stopped) {
        done?.();
        return;
      }
      onStopped = done;
      fader.to(0, fadeSeconds);
      const id = env.deps.setTimer(() => {
        retiring.delete(id);
        stop();
      }, (fadeSeconds + RETIRE_MARGIN_S) * MS_PER_S);
      retiring.add(id);
    },
    stop,
  };
}
